import { useState } from 'react'
import { v4 as uuid } from 'uuid'
import { db, timestamp } from '../../firebase/config'
import { client } from '../../hooks/Client'
import { useOutputGoal } from '../../hooks/useKpis'
import Modal from '../common/Modal'

/**
 * Goal for a countable output: a target number + a target date. One Kpis doc
 * (Type 'output') per output. See docs/features/msi-effect-goals.md.
 *
 * @param {object} props
 * @param {boolean} props.modalOpen - Whether the modal is visible
 * @param {(open: boolean) => void} props.setModalOpen - Modal visibility setter
 * @param {{ID: string, Title?: string} | null} props.output - Outputs doc
 * @param {(message: string) => void} [props.onSaved] - Optional saved-notice callback
 */
const OutputGoalModal = ({ modalOpen, setModalOpen, output, onSaved }) => {
    /** @type {[{value?: string, deadline?: string}, Function]} */
    const [draft, setDraft] = useState({})

    const { goal } = useOutputGoal(output?.ID || '')

    // Draft falls back to the saved goal so inputs show existing values.
    const valueFor = (key) => {
        if (draft[key] !== undefined) return draft[key]
        if (!goal) return ''
        const saved = key === 'value' ? goal.TargetValue : goal.Deadline
        return saved === undefined || saved === null ? '' : String(saved)
    }

    const draftHandler = (key, value) => {
        setDraft((prev) => ({ ...prev, [key]: value }))
    }

    const targetRaw = valueFor('value')
    const deadline = valueFor('deadline')
    const targetNumber = Number(targetRaw)

    const bothEmpty = targetRaw === '' && deadline === ''
    const hasValidTarget = targetRaw !== '' && Number.isFinite(targetNumber) && targetNumber >= 0
    // A goal needs both a number and a date; empty clears it.
    const canSave = bothEmpty || (hasValidTarget && deadline !== '')

    const saveGoal = async () => {
        if (bothEmpty) {
            if (goal) {
                await db.collection('Kpis').doc(goal.docid).update({
                    Deleted: true,
                    DeletedAt: timestamp,
                })
            }
        } else if (goal) {
            await db.collection('Kpis').doc(goal.docid).update({
                TargetValue: targetNumber,
                Deadline: deadline,
            })
        } else {
            await db.collection('Kpis').doc().set({
                ID: uuid(),
                Compagny: client,
                CompagnyID: client,
                Type: 'output',
                OutputID: output.ID,
                TargetValue: targetNumber,
                Deadline: deadline,
                Timestamp: timestamp,
            })
        }

        setDraft({})
        if (onSaved) onSaved('flex')
        setModalOpen(false)
    }

    return (
        <Modal
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            pageHeader='Doel instellen'
            description={`Stel een streefgetal en een streefdatum in voor deze output. Laat beide velden leeg om geen doel te stellen.`}
            confirm={saveGoal}
            confirmDisabled={!canSave}
            content={
                <div className='output-goal-modal'>
                    <label className='output-goal-modal-field'>
                        <span>Streefgetal</span>
                        <input
                            type='number'
                            min={0}
                            step={1}
                            value={targetRaw}
                            onChange={(e) => draftHandler('value', e.target.value)}
                        />
                    </label>
                    <label className='output-goal-modal-field'>
                        <span>Streefdatum</span>
                        <input
                            type='date'
                            value={deadline}
                            onChange={(e) => draftHandler('deadline', e.target.value)}
                        />
                    </label>
                    {!canSave && (
                        <p className='output-goal-modal-hint'>Vul zowel een getal als een datum in.</p>
                    )}
                </div>
            }
        />
    )
}

export default OutputGoalModal
